import { Box } from '@mui/material';
import { PlatformGlyph, getPlatformMeta } from '@/components/PlatformIcon';

/**
 * The marketing site's hero motif: concentric rings with social network chips
 * orbiting a soft colour glow.
 *
 * Built with CSS transforms only — no canvas, no animation library, nothing to
 * load. Each ring rotates as a whole; every chip counter-rotates at the same
 * speed so the logos stay upright while travelling the circle. Rings alternate
 * direction and use non-multiple durations so the composition never visibly
 * repeats.
 *
 * Purely decorative: aria-hidden, pointer-events none, and it stops entirely
 * for anyone who has asked for reduced motion.
 */

const CHIP = 52;

// radius: distance from centre. angle: starting position in degrees, 0 = right.
const ORBITS = [
  {
    radius: 210,
    duration: 54,
    reverse: false,
    chips: [
      { platform: 'facebook', angle: 168, size: 26 },
      { platform: 'instagram', angle: 22, size: 26 },
    ],
  },
  {
    radius: 300,
    duration: 72,
    reverse: true,
    chips: [
      { platform: 'telegram', angle: 316, size: 25 },
      { platform: 'threads', angle: 96, size: 23 },
      { platform: 'x', angle: 208, size: 22 },
    ],
  },
  {
    radius: 400,
    duration: 92,
    reverse: false,
    chips: [
      { platform: 'snapchat', angle: 258, size: 26 },
      { platform: 'linkedin', angle: 330, size: 24 },
      { platform: 'youtube', angle: 140, size: 26 },
    ],
  },
  {
    radius: 505,
    duration: 116,
    reverse: true,
    chips: [
      { platform: 'pinterest', angle: 44, size: 24 },
      { platform: 'tiktok', angle: 186, size: 23 },
    ],
  },
];

// Faint rings drawn behind everything, including a few with no chips on them.
const RING_RADII = [150, 210, 300, 400, 505, 610];

/**
 * Brand colour, but legible on a white chip.
 *
 * A few brands are very light — Snapchat's #FFFC00 all but disappears on white.
 * Where luminance is too high we darken the colour until it reads, rather than
 * dropping the brand identity entirely.
 */
function chipTone(platform) {
  const hex = String(getPlatformMeta(platform).color || '#666').replace('#', '');
  if (hex.length < 6) return '6B7280';
  let r = parseInt(hex.slice(0, 2), 16);
  let g = parseInt(hex.slice(2, 4), 16);
  let b = parseInt(hex.slice(4, 6), 16);
  let lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // Darken in steps until it clears the legibility threshold.
  let guard = 0;
  while (lum > 0.62 && guard < 12) {
    r = Math.round(r * 0.82);
    g = Math.round(g * 0.82);
    b = Math.round(b * 0.82);
    lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    guard += 1;
  }
  return [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

export default function OrbitBackdrop({ sx = {} }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // One pair of keyframes drives every ring and every counter-rotation.
        '@keyframes orbitSpin': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        },
        '@keyframes orbitGlow': {
          '0%, 100%': { opacity: 0.85, transform: 'translate(-50%, -50%) scale(1)' },
          '50%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1.06)' },
        },
        // Respect the OS setting — a decorative loop is exactly what this is for.
        '@media (prefers-reduced-motion: reduce)': {
          '& *': { animation: 'none !important' },
        },
        ...sx,
      }}
    >
      {/* Centre glow: cyan core bleeding into lavender, as on the site. */}
      <Box
        sx={{
          position: 'absolute', top: '50%', left: '50%',
          width: 760, height: 760, borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          background:
            'radial-gradient(circle at 50% 52%, rgba(168,232,246,.55) 0%, rgba(186,190,247,.5) 32%, rgba(199,190,245,.32) 52%, rgba(233,235,246,0) 72%)',
          filter: 'blur(6px)',
          animation: 'orbitGlow 9s ease-in-out infinite',
        }}
      />

      {/* Static rings */}
      {RING_RADII.map((r) => (
        <Box
          key={`ring-${r}`}
          sx={{
            position: 'absolute',
            width: r * 2, height: r * 2, borderRadius: '50%',
            border: '1px solid rgba(120,130,190,.13)',
          }}
        />
      ))}

      {/* Orbiting chips */}
      {ORBITS.map((orbit) => (
        <Box
          key={`orbit-${orbit.radius}`}
          sx={{
            position: 'absolute',
            width: orbit.radius * 2,
            height: orbit.radius * 2,
            animation: `orbitSpin ${orbit.duration}s linear infinite${orbit.reverse ? ' reverse' : ''}`,
          }}
        >
          {orbit.chips.map((chip) => (
            <Box
              key={chip.platform}
              sx={{
                position: 'absolute', top: '50%', left: '50%',
                width: CHIP, height: CHIP,
                ml: `${-CHIP / 2}px`, mt: `${-CHIP / 2}px`,
                // Walk out to the ring at this chip's starting angle.
                transform: `rotate(${chip.angle}deg) translateX(${orbit.radius}px) rotate(${-chip.angle}deg)`,
              }}
            >
              {/* Same speed, opposite direction — the logo stays upright. */}
              <Box
                sx={{
                  width: '100%', height: '100%', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: '#fff',
                  boxShadow: '0 6px 18px rgba(30,42,94,.14), 0 1px 3px rgba(30,42,94,.08)',
                  animation: `orbitSpin ${orbit.duration}s linear infinite${orbit.reverse ? '' : ' reverse'}`,
                }}
              >
                {/* Brand colour, not the default contrast tone — these chips
                    are white, so the contrast tone would render white on white. */}
                <PlatformGlyph platform={chip.platform} size={chip.size} tone={chipTone(chip.platform)} />
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
